import React, { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import {
  LuArrowLeft,
  LuArrowRight,
  LuBuilding2,
  LuCalendarDays,
  LuClipboardList,
  LuClock3,
  LuFileText,
  LuHeart,
  LuLayoutDashboard,
  LuMail,
  LuMessageCircle,
  LuPackage,
  LuRotateCcw,
  LuSearch,
  LuShoppingCart,
  LuTicket,
  LuTruck,
  LuUser,
  LuWallet,
  LuWrench,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HelpSupportIllustration from "../../components/help/HelpSupportIllustration";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/help_center.scss";
import "../../assets/styles/help_topic_support.scss";

const BUYER_ARTICLES = [
  {
    id: "create-rfq",
    title: "How to Create RFQ",
    description: "Learn how to post a sourcing request and invite suppliers.",
    updated: "10 Jul 2026",
    time: "3 min read",
    icon: LuFileText,
    tone: "blue",
    tag: "Create RFQ",
  },
  {
    id: "compare-quotes",
    title: "Compare Quotations",
    description: "Review and compare quotes from multiple suppliers.",
    updated: "08 Jul 2026",
    time: "4 min read",
    icon: LuWallet,
    tone: "green",
    tag: "Compare Quotes",
  },
  {
    id: "manage-po",
    title: "Manage Purchase Orders",
    description: "Create, manage and track your purchase orders.",
    updated: "06 Jul 2026",
    time: "4 min read",
    icon: LuClipboardList,
    tone: "orange",
    tag: "Purchase Orders",
  },
  {
    id: "track-orders",
    title: "Track Orders",
    description: "Track your order status from processing to delivery.",
    updated: "06 Jul 2026",
    time: "3 min read",
    icon: LuTruck,
    tone: "purple",
    tag: "Track Orders",
  },
  {
    id: "returns-refunds",
    title: "Returns & Refunds",
    description: "Learn about return requests, refunds and replacement.",
    updated: "04 Jul 2026",
    time: "3 min read",
    icon: LuRotateCcw,
    tone: "red",
    tag: "Returns & Refunds",
  },
  {
    id: "buyer-dashboard",
    title: "Buyer Dashboard Guide",
    description: "Overview of buyer dashboard features and tools.",
    updated: "03 Jul 2026",
    time: "2 min read",
    icon: LuLayoutDashboard,
    tone: "cyan",
    tag: "Account Settings",
  },
  {
    id: "favorite-suppliers",
    title: "Favorite Suppliers",
    description: "Save and manage your favorite suppliers easily.",
    updated: "02 Jul 2026",
    time: "2 min read",
    icon: LuHeart,
    tone: "yellow",
    tag: "Account Settings",
  },
  {
    id: "buyer-account",
    title: "Buyer Account Settings",
    description: "Update profile information, users and notification preferences.",
    updated: "01 Jul 2026",
    time: "3 min read",
    icon: LuUser,
    tone: "blue",
    tag: "Account Settings",
  },
];

const TOPICS = {
  buyer: {
    title: "Buyer Support",
    description: "Find help on RFQs, quotations, orders and more.",
    icon: LuShoppingCart,
    articleCount: 18,
    articles: BUYER_ARTICLES,
  },
  seller: {
    title: "Seller Support",
    description: "Registration, verification and catalog support.",
    icon: LuPackage,
    articleCount: 15,
    articles: [
      {
        id: "seller-register",
        title: "Register as a Seller",
        description: "Create your seller account and company profile.",
        updated: "10 Jul 2026",
        time: "3 min read",
        icon: LuUser,
        tone: "blue",
        tag: "Registration",
      },
      {
        id: "verify-seller",
        title: "Verify Seller Account",
        description: "Complete KYC to unlock catalog features.",
        updated: "09 Jul 2026",
        time: "4 min read",
        icon: LuFileText,
        tone: "green",
        tag: "Verification",
      },
      {
        id: "upload-products",
        title: "Upload Products",
        description: "Add listings with images, specs and pricing.",
        updated: "08 Jul 2026",
        time: "4 min read",
        icon: LuPackage,
        tone: "orange",
        tag: "Catalog",
      },
    ],
  },
  "orders-payments": {
    title: "Orders & Payments",
    description: "Orders, invoices, refunds and payment support.",
    icon: LuWallet,
    articleCount: 12,
    articles: [
      {
        id: "track-orders",
        title: "Track Orders",
        description: "Follow order status from quote to delivery.",
        updated: "08 Jul 2026",
        time: "3 min read",
        icon: LuTruck,
        tone: "purple",
        tag: "Orders",
      },
      {
        id: "payment-issues",
        title: "Payment Issues",
        description: "Resolve failed payments and invoice problems.",
        updated: "07 Jul 2026",
        time: "4 min read",
        icon: LuWallet,
        tone: "orange",
        tag: "Payments",
      },
    ],
  },
  "contract-manufacturing": {
    title: "Contract Manufacturing",
    description: "Manufacturing requests and production support.",
    icon: LuBuilding2,
    articleCount: 9,
    articles: [
      {
        id: "cm-rfq",
        title: "Send Manufacturing RFQ",
        description: "Request OEM/ODM production from verified partners.",
        updated: "09 Jul 2026",
        time: "4 min read",
        icon: LuClipboardList,
        tone: "blue",
        tag: "RFQ",
      },
      {
        id: "cm-production",
        title: "Track Production",
        description: "Follow manufacturing milestones and deliveries.",
        updated: "07 Jul 2026",
        time: "3 min read",
        icon: LuBuilding2,
        tone: "green",
        tag: "Production",
      },
    ],
  },
  logistics: {
    title: "Logistics",
    description: "Shipment booking and tracking support.",
    icon: LuTruck,
    articleCount: 11,
    articles: [
      {
        id: "book-shipment",
        title: "Book a Shipment",
        description: "Request freight quotes from logistics providers.",
        updated: "09 Jul 2026",
        time: "4 min read",
        icon: LuClipboardList,
        tone: "blue",
        tag: "Booking",
      },
      {
        id: "track-shipment",
        title: "Track Shipment",
        description: "Monitor pickup, transit and delivery updates.",
        updated: "06 Jul 2026",
        time: "3 min read",
        icon: LuTruck,
        tone: "purple",
        tag: "Tracking",
      },
    ],
  },
  technical: {
    title: "Technical Support",
    description: "Platform issues and troubleshooting support.",
    icon: LuWrench,
    articleCount: 10,
    articles: [
      {
        id: "login-issues",
        title: "Login Issues",
        description: "Fix sign-in problems and reset access.",
        updated: "08 Jul 2026",
        time: "3 min read",
        icon: LuUser,
        tone: "blue",
        tag: "Login",
      },
      {
        id: "report-bug",
        title: "Report a Platform Issue",
        description: "Raise a ticket for errors or unexpected behavior.",
        updated: "05 Jul 2026",
        time: "3 min read",
        icon: LuWrench,
        tone: "red",
        tag: "Troubleshooting",
      },
    ],
  },
};

const BUYER_GUIDES = [
  { title: "Buyer Guide", description: "Complete guide for buyers", icon: LuFileText, tone: "blue" },
  { title: "Payments & Invoices", description: "Payment methods and invoices", icon: LuWallet, tone: "cyan" },
  { title: "Disputes & Resolution", description: "How disputes are handled", icon: LuMessageCircle, tone: "yellow" },
  { title: "Supplier Verification", description: "How we verify suppliers", icon: LuUser, tone: "green" },
];

export default function HelpCenterTopicPage() {
  const { topicId } = useParams();
  const topic = TOPICS[topicId];
  const [query, setQuery] = useState("");
  const [activeTag, setActiveTag] = useState("");

  const filteredArticles = useMemo(() => {
    if (!topic) return [];
    const normalizedQuery = query.trim().toLowerCase();
    return topic.articles.filter((article) => {
      const tagMatch = !activeTag || article.tag === activeTag;
      const queryMatch =
        !normalizedQuery ||
        `${article.title} ${article.description} ${article.tag}`
          .toLowerCase()
          .includes(normalizedQuery);
      return tagMatch && queryMatch;
    });
  }, [activeTag, query, topic]);

  if (!topic) {
    return <Navigate to="/help-center" replace />;
  }

  const Icon = topic.icon;
  const tags = [...new Set(topic.articles.map((article) => article.tag))];
  const guides = topicId === "buyer" ? BUYER_GUIDES : BUYER_GUIDES.slice(0, 3);

  return (
    <div className="help-center-page help-topic-support-page">
      <B2BHubHomeHeader />
      <main className="help-topic-support">
        <nav className="help-topic-support__breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">{topic.title}</span>
        </nav>

        <header className="help-topic-support__head">
          <div className="help-topic-support__title">
            <span className="help-topic-support__title-icon" aria-hidden>
              <Icon />
            </span>
            <div>
              <h1>{topic.title}</h1>
              <p>{topic.description}</p>
            </div>
          </div>
          <Link to="/help-center" className="help-topic-support__back">
            <LuArrowLeft aria-hidden /> Back to Help Center
          </Link>
        </header>

        <section className="help-topic-support__toolbar">
          <label className="help-topic-support__search">
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={`Search within ${topic.title}...`}
            />
            <LuSearch aria-hidden />
          </label>
          <div className="help-topic-support__stats">
            <div>
              <span className="help-topic-support__stat-icon help-topic-support__stat-icon--blue">
                <LuFileText aria-hidden />
              </span>
              <strong>{topic.articleCount}</strong>
              <small>Articles</small>
            </div>
            <div>
              <span className="help-topic-support__stat-icon help-topic-support__stat-icon--cyan">
                <LuCalendarDays aria-hidden />
              </span>
              <strong>Updated</strong>
              <small>Weekly</small>
            </div>
            <div>
              <span className="help-topic-support__stat-icon help-topic-support__stat-icon--green">
                <LuClock3 aria-hidden />
              </span>
              <strong>3 min</strong>
              <small>Avg. Read Time</small>
            </div>
          </div>
        </section>

        <section className="help-topic-support__popular">
          <h2>Popular Topics</h2>
          <div className="help-topic-support__chips">
            {tags.map((tag, index) => {
              const article = topic.articles.find((item) => item.tag === tag);
              const TagIcon = article?.icon || LuFileText;
              return (
                <button
                  key={tag}
                  type="button"
                  className={activeTag === tag ? "is-active" : ""}
                  onClick={() => setActiveTag((current) => (current === tag ? "" : tag))}
                >
                  <span className={`help-topic-support__chip-icon help-topic-tone--${article?.tone || "blue"}`}>
                    <TagIcon aria-hidden />
                  </span>
                  {tag}
                  {index === 0 ? null : ""}
                </button>
              );
            })}
          </div>
        </section>

        <section className="help-topic-support__content">
          <div className="help-topic-support__articles-wrap">
            <h2>Browse Help Articles</h2>
            {filteredArticles.length ? (
              <div className="help-topic-support__articles">
                {filteredArticles.map((article) => {
                  const ArticleIcon = article.icon || LuFileText;
                  return (
                    <article key={article.id} className="help-topic-support__article">
                      <div className="help-topic-support__article-main">
                        <span className={`help-topic-support__article-icon help-topic-tone--${article.tone}`} aria-hidden>
                          <ArticleIcon />
                        </span>
                        <div>
                          <h3>{article.title}</h3>
                          <p>{article.description}</p>
                        </div>
                      </div>
                      <div className="help-topic-support__article-meta">
                        <span>{article.time}</span>
                        <i aria-hidden>•</i>
                        <span>Updated {article.updated}</span>
                      </div>
                      <Link to={`/help-center/articles/${article.id}`}>
                        Read More <LuArrowRight aria-hidden />
                      </Link>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="help-topic-support__empty">No matching help articles found.</div>
            )}
            <Link to="/help-center/articles" className="help-topic-support__view-all">
              View All {topic.title} Articles <LuArrowRight aria-hidden />
            </Link>
          </div>

          <aside className="help-topic-support__guides">
            <h2>Related Guides</h2>
            <div>
              {guides.map((guide) => {
                const GuideIcon = guide.icon;
                return (
                  <Link key={guide.title} to="/help-center/articles">
                    <span className={`help-topic-support__guide-icon help-topic-tone--${guide.tone}`} aria-hidden>
                      <GuideIcon />
                    </span>
                    <span>
                      <strong>{guide.title}</strong>
                      <small>{guide.description}</small>
                    </span>
                  </Link>
                );
              })}
            </div>
            <Link to="/help-center/articles" className="help-topic-support__guides-all">
              View All Guides <LuArrowRight aria-hidden />
            </Link>
          </aside>
        </section>

        <section className="help-topic-support__cta">
          <div className="help-topic-support__cta-visual">
            <HelpSupportIllustration />
          </div>
          <div className="help-topic-support__cta-copy">
            <h2>Still need help?</h2>
            <p>Our support team is here to assist you.</p>
          </div>
          <div className="help-topic-support__cta-actions">
            <Link to="/help-center/raise-ticket" className="help-topic-support__cta-primary">
              <LuTicket aria-hidden /> Raise Ticket
            </Link>
            <Link to="/contact?enquiry=general">
              <LuMessageCircle aria-hidden /> Live Chat
            </Link>
            <a href="mailto:support@trocanet.com">
              <LuMail aria-hidden /> Email Support
            </a>
          </div>
        </section>
      </main>
      <HubHomeFooter />
    </div>
  );
}
